import React, { useMemo } from 'react';
import { TwoPanelsPageLayout, Heading, Paragraph, Button } from '@expressable/syntax-ds';
import { useTranslation } from 'react-i18next';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { useEffectOnce } from 'react-use';
import { useTrack } from 'utils/hooks/useMixPanel';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { ChatsCircle, Money, Files, IdentificationCard, Info } from '@phosphor-icons/react';
import Scratch from 'assets/images/Scratch.svg';

const SelfPayPage: React.FC = () => {
  const { t } = useTranslation();
  const { filters, isResumedLead, onSubmitFilters, switchToPrivatePay, setHasSwitchedToPrivatePay } =
    useSignupDirectCtx();
  const { track } = useTrack();

  const iconMap = useMemo(
    () =>
      new Map<number, React.ReactNode>([
        [1, <ChatsCircle size={32} className="shrink-0" />],
        [2, <Money size={32} className="shrink-0" />],
        [3, <Files size={32} className="shrink-0" />],
        [4, <IdentificationCard size={32} className="shrink-0" />],
      ]),
    [],
  );

  useEffectOnce(() => {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.REDIRECTED_TO_SELF_PAY.LANDING_PAGE_VIEWED, { isResumedLead, filters });
  });

  return (
    <TwoPanelsPageLayout>
      <TwoPanelsPageLayout.LeftPanel className="px-64 xl:px-80 gap-64">
        <div>
          <Heading level="h2">{t('directSignup.selfPay.leftPanel.heading')}</Heading>
          <Paragraph>{t('directSignup.selfPay.leftPanel.copy')}</Paragraph>
        </div>

        <Button onClick={() => switchToPrivatePay(filters, onSubmitFilters, setHasSwitchedToPrivatePay)}>
          {t('directSignup.selfPay.leftPanel.cta')}
        </Button>
      </TwoPanelsPageLayout.LeftPanel>

      <TwoPanelsPageLayout.RightPanel className="px-64 xl:px-80 gap-24 tablet-d:gap-24">
        <div>
          <Heading level="h2" className="text-center">
            {t('directSignup.selfPay.rightPanel.heading')}
          </Heading>
          <img alt="" className="h-auto w-[65%] max-w-[200px] m-auto" src={Scratch} />
        </div>

        {[...iconMap.entries()].map(([bulletNumber, icon]) => (
          <Paragraph key={bulletNumber} noDefaultMargins className="flex gap-16 items-center">
            {icon}
            <span className="flex flex-col">
              <span className="font-semibold">
                {t(`directSignup.selfPay.rightPanel.bullets.${bulletNumber}.title`)}
              </span>
              {t(`directSignup.selfPay.rightPanel.bullets.${bulletNumber}.description`)}
            </span>
          </Paragraph>
        ))}

        <hr className="my-24 border-gray-300" />

        <div className="flex gap-16">
          <Info size={20} weight="fill" className="shrink-0" />
          {t('directSignup.selfPay.rightPanel.bullets.highlight')}
        </div>
      </TwoPanelsPageLayout.RightPanel>
    </TwoPanelsPageLayout>
  );
};

export default SelfPayPage;
